import { useState, useEffect } from "react";
import { activate } from "../../../utils/keyboard-activate";
import type { CrmMediaResponse } from "../../../types/crm";
import { getMediaForProfile } from "../../../services/crmMedia";
import "./MediaBrowserModal.css";

interface MediaBrowserModalProps {
  isOpen: boolean;
  crmProspectProfileId: string;
  onSelect: (media: CrmMediaResponse) => void;
  onClose: () => void;
}

export const MediaBrowserModal = ({
  isOpen,
  crmProspectProfileId,
  onSelect,
  onClose,
}: MediaBrowserModalProps) => {
  const [media, setMedia] = useState<CrmMediaResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CrmMediaResponse | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSelected(null);
    getMediaForProfile(crmProspectProfileId)
      .then(setMedia)
      .catch(() => setMedia([]))
      .finally(() => setLoading(false));
  }, [isOpen, crmProspectProfileId]);

  if (!isOpen) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="media-browser-modal__overlay" onClick={onClose}>
      <div className="media-browser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="media-browser-modal__header">
          <h3>Select Media</h3>
          <button className="media-browser-modal__close" onClick={onClose}>×</button>
        </div>

        <div className="media-browser-modal__body">
          {loading ? (
            <div className="media-browser-modal__loading">Loading media...</div>
          ) : media.length === 0 ? (
            <div className="media-browser-modal__empty">
              This player has no media available.
            </div>
          ) : (
            <div className="media-browser-modal__grid">
              {media.map((item) => (
                <div
                  key={item.id}
                  className={`media-browser-modal__item ${selected?.id === item.id ? "media-browser-modal__item--selected" : ""}`}
                  {...activate(() => setSelected(item))}
                >
                  <div className="media-browser-modal__thumb-wrapper">
                    {item.thumbnailUrl ? (
                      <img
                        src={item.thumbnailUrl}
                        alt={item.originalFileName || "Media"}
                        className="media-browser-modal__thumb"
                      />
                    ) : (
                      <div className="media-browser-modal__thumb-placeholder">
                        {item.mediaType === "Video" ? "🎬" : "🖼"}
                      </div>
                    )}
                    {item.mediaType === "Video" && (
                      <span className="media-browser-modal__type-badge">VIDEO</span>
                    )}
                  </div>
                  <div className="media-browser-modal__item-info">
                    <span className="media-browser-modal__item-name">
                      {item.originalFileName || `${item.mediaType} ${item.fileExtension}`}
                    </span>
                    <span className="media-browser-modal__item-size">
                      {formatFileSize(item.fileSizeBytes)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="media-browser-modal__footer">
          <button className="media-browser-modal__btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="media-browser-modal__btn-attach"
            disabled={!selected}
            onClick={() => selected && onSelect(selected)}
          >
            Attach
          </button>
        </div>
      </div>
    </div>
  );
};
